<template>
  <div class="p-2 sm:p-4 max-w-7xl mx-auto">
    <!-- Header with Date Range -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
      <h1 class="text-xl sm:text-2xl font-bold text-gray-800">Dashboard de Productos</h1>
      <div class="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
        <div class="grid grid-cols-2 gap-2">
          <input type="date" v-model="fecha_inicio" class="text-sm sm:text-base px-3 py-1.5 border rounded w-full">
          <input type="date" v-model="fecha_fin" class="text-sm sm:text-base px-3 py-1.5 border rounded w-full">
        </div>
        <button @click="cargarDatos" class="px-4 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm sm:text-base">
          Consultar
        </button>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div class="bg-white rounded-lg shadow p-3 sm:p-4">
      <h2 class="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Análisis de Productos</h2>
      <p class="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
        Información detallada sobre el rendimiento de productos, ventas y categorías.
      </p>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>

      <!-- Main Content -->
      <div v-if="!loading && !error" class="space-y-4">
        <!-- Top Cards Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <!-- Producto Más Vendido -->
          <div class="border rounded-lg p-3 h-[300px] sm:h-auto flex flex-col">
            <h3 class="font-semibold text-sm sm:text-base mb-2">Producto Más Vendido</h3>
            <div v-if="productoMasVendido" class="flex-1 flex flex-col justify-center p-3 bg-blue-50 rounded">
              <p class="text-sm sm:text-base font-medium text-gray-800 mb-2 line-clamp-2">{{ productoMasVendido.producto }}</p>
              <div class="mt-auto">
                <p class="text-xl sm:text-2xl font-bold text-blue-600">{{ parseFloat(productoMasVendido.total_vendido).toLocaleString('es-MX') }}</p>
                <p class="text-xs sm:text-sm text-gray-500">unidades vendidas</p>
              </div>
            </div>
            <div v-else class="h-full flex items-center justify-center text-gray-400">
              No hay datos
            </div>
          </div>

          <!-- Resumen de Ventas -->
          <div class="border rounded-lg p-3 h-[300px] sm:h-auto">
            <h3 class="font-semibold text-sm sm:text-base mb-2">Resumen de Ventas</h3>
            <div class="h-[calc(100%-2rem)] grid grid-cols-2 gap-3">
              <div class="p-3 bg-green-50 rounded flex flex-col justify-center">
                <p class="text-xs sm:text-sm text-gray-600">Total Unidades</p>
                <p class="text-lg sm:text-xl font-bold text-green-600">{{ resumenVentas.totalUnidades.toLocaleString('es-MX') }}</p>
              </div>
              <div class="p-3 bg-purple-50 rounded flex flex-col justify-center">
                <p class="text-xs sm:text-sm text-gray-600">Monto Total</p>
                <p class="text-lg sm:text-xl font-bold text-purple-600">${{ resumenVentas.montoTotal.toLocaleString('es-MX') }}</p>
              </div>
              <!-- Add an empty div to maintain grid structure -->
              <div class="col-span-2"></div>
            </div>
          </div>

          </div>
        
        <!-- Pie Charts Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Unidades Vendidas Pie Chart -->
          <div class="border rounded-lg p-3">
            <h3 class="font-semibold text-sm sm:text-base mb-2">Distribución por Unidades Vendidas</h3>
            <div style="width: 100%; min-height: 300px; position: relative;">
              <div v-if="hasValidData('unidades')" style="width: 100%; height: 300px;">
                <apexchart 
                  type="donut" 
                  :options="getChartOptions('unidades')"
                  :series="getChartSeries('unidades')"
                  width="100%"
                  height="300"
                />
              </div>
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No hay datos disponibles
              </div>
            </div>
          </div>
          
          <!-- Monto Vendido Pie Chart -->
          <div class="border rounded-lg p-3">
            <h3 class="font-semibold text-sm sm:text-base mb-2">Distribución por Monto Vendido</h3>
            <div style="width: 100%; min-height: 300px; position: relative;">
              <div v-if="hasValidData('monto')" style="width: 100%; height: 300px;">
                <apexchart 
                  type="donut" 
                  :options="getChartOptions('monto')"
                  :series="getChartSeries('monto')"
                  width="100%"
                  height="300"
                />
              </div>
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No hay datos disponibles
              </div>
            </div>
          </div>
        </div>
        
        <!-- Categorias Bar Chart -->
        <div class="border rounded-lg p-3 h-[350px] sm:h-auto">
            <h3 class="font-semibold text-sm sm:text-base mb-2">Ventas por Categoría</h3>
            <div class="h-[calc(100%-2rem)]">
              <apexchart 
                type="bar" 
                :options="chartOptions"
                :series="seriesCategorias"
                height="100%"
              ></apexchart>
            </div>
          </div>
        <!-- Table Section -->
        <div class="border rounded-lg p-3 h-[300px] sm:h-auto overflow-hidden flex flex-col">
          <div class="flex justify-between items-center mb-2">
            <h3 class="font-semibold text-sm sm:text-base">Ventas por Producto</h3>
            <span class="text-xs text-gray-500">{{ ventasPorProducto.length }} productos</span>
          </div>
          <div class="flex-1 overflow-auto">
            <table class="min-w-full bg-white text-xs sm:text-sm">
              <thead class="sticky top-0 bg-white">
                <tr class="bg-gray-50">
                  <th class="py-2 px-2 text-left font-medium text-gray-500">Producto</th>
                  <th class="py-2 px-2 text-right font-medium text-gray-500">Unidades</th>
                  <th class="py-2 px-2 text-right font-medium text-gray-500">Monto Total</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-if="ventasPorProducto.length === 0">
                  <td colspan="3" class="py-4 text-center text-gray-400">No hay datos disponibles</td>
                </tr>
                <tr v-for="(item, index) in ventasPorProducto" :key="item.producto" 
                    :class="{ 'bg-gray-50': index % 2 === 0 }">
                  <td class="py-2 px-2 text-gray-800 truncate max-w-[200px]">{{ item.producto }}</td>
                  <td class="py-2 px-2 text-right text-gray-600">{{ parseInt(item.unidades_vendidas).toLocaleString('es-MX') }}</td>
                  <td class="py-2 px-2 text-right font-medium">${{ parseFloat(item.total_vendido).toLocaleString('es-MX') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="error" class="text-center py-6 text-red-500 text-sm sm:text-base">
        <p>{{ error }}</p>
        <button @click="cargarDatos" class="mt-2 px-4 py-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 text-sm">
          Reintentar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import reportesNuevos from '@/Services/reportesNuevos.js';
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'DashboardProductos',
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);

    return {
      fecha_inicio: startDate.toISOString().split('T')[0],
      fecha_fin: endDate.toISOString().split('T')[0],
      loading: false,
      error: null,
      ventasPorProducto: [],
      productoMasVendido: null,
      ventasPorCategoria: [],
      seriesCategorias: [{ data: [] }],
      chartInitialized: false,
      chartOptionsCategorias: {
        chart: { type: 'bar' },
        xaxis: { categories: [] },
        dataLabels: { enabled: false },
        tooltip: {
          y: {
            formatter: function (val) {
              return "$" + val.toLocaleString('es-MX')
            }
          }
        }
      },
      hasData: false,
    };
  },
  computed: {
    // Top 5 productos para las gráficas de pastel
    topProductos() {
      if (!this.ventasPorProducto || this.ventasPorProducto.length === 0) return [];
      return [...this.ventasPorProducto]
        .sort((a, b) => b.unidades_vendidas - a.unidades_vendidas)
        .slice(0, 5);
    },
    
    // Métodos para gráficos
    pieSeriesUnits() {
      return this.getChartSeries('unidades');
    },
    pieSeriesAmount() {
      return this.getChartSeries('monto');
    },
    pieChartOptionsUnits() {
      return this.getChartOptions('unidades');
    },
    pieChartOptionsAmount() {
      return this.getChartOptions('monto');
    },
    
    resumenVentas() {
      if (!this.ventasPorProducto || this.ventasPorProducto.length === 0) {
        return { totalUnidades: 0, montoTotal: 0 };
      }
      const totalUnidades = this.ventasPorProducto.reduce((sum, item) => sum + parseInt(item.unidades_vendidas), 0);
      const montoTotal = this.ventasPorProducto.reduce((sum, item) => sum + parseFloat(item.total_vendido), 0);
      return { totalUnidades, montoTotal };
    },
    chartOptions() {
      return {
        ...this.chartOptionsCategorias,
        chart: { 
          ...this.chartOptionsCategorias.chart,
          toolbar: { show: false },
          fontFamily: 'Inter, sans-serif',
          zoom: { enabled: false },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          }
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: { 
            horizontal: false,
            columnWidth: '70%',
            borderRadius: 4,
            distributed: false
          }
        },
        xaxis: {
          ...this.chartOptionsCategorias.xaxis,
          labels: { 
            style: { 
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif',
              colors: '#6b7280'
            },
            rotate: -45,
            rotateAlways: false,
            hideOverlappingLabels: true,
            trim: true
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          tooltip: {
            enabled: false
          }
        },
        yaxis: {
          labels: { 
            style: { 
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif',
              colors: '#6b7280'
            },
            formatter: function(value) {
              return '$' + value.toLocaleString('es-MX');
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return "$" + val.toLocaleString('es-MX');
            }
          },
          style: {
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif'
          },
          theme: 'light',
          fillSeriesColor: false,
          onDatasetHover: {
            highlightDataSeries: true,
          },
          x: {
            show: true,
            format: 'dd MMM yyyy',
            formatter: undefined,
          },
          marker: {
            show: true,
          },
        },
        grid: {
          borderColor: '#e5e7eb',
          strokeDashArray: 4,
          yaxis: {
            lines: {
              show: true
            }
          },
          padding: {
            top: 0,
            right: 20,
            bottom: 0,
            left: 20
          },
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.05,
            }
          },
          active: {
            filter: {
              type: 'darken',
              value: 0.5,
            }
          }
        },
        stroke: {
          width: 2,
          colors: ['#fff'],
          lineCap: 'round'
        },
        fill: {
          opacity: 0.9,
          type: 'solid',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.3,
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 0.8,
            stops: [0, 100]
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          itemMargin: {
            horizontal: 10,
            vertical: 5
          },
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          onItemClick: {
            toggleDataSeries: true
          },
          onItemHover: {
            highlightDataSeries: true
          },
        },
        responsive: [{
          breakpoint: 768,
          options: {
            plotOptions: {
              bar: {
                columnWidth: '50%'
              }
            },
            legend: {
              position: 'bottom',
              horizontalAlign: 'center'
            }
          }
        }]
      };
    }
  },
  methods: {
    // Verificar si hay datos válidos para mostrar
    hasValidData(type) {
      if (!Array.isArray(this.ventasPorProducto) || this.ventasPorProducto.length === 0) {
        return false;
      }
      
      const hasValidValues = this.ventasPorProducto.some(item => {
        const value = type === 'monto' 
          ? parseFloat(item.total_vendido) 
          : parseInt(item.unidades_vendidas, 10);
        return !isNaN(value) && value > 0;
      });
      
      return hasValidValues;
    },
    
    // Obtener series para el gráfico
   
    
    
    // Obtener opciones del gráfico
    getChartOptions(type) {
      const isMonto = type === 'monto';
      const series = this.getChartSeries(type);
      const total = series.reduce((a, b) => a + b, 0);
      
      return {
        chart: {
          type: 'donut',
          fontFamily: 'Inter, sans-serif',
          toolbar: { show: true },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          },
          width: '100%',
          height: 300,
          sparkline: { enabled: false },
          stacked: false
        },
        labels: Array.isArray(this.ventasPorProducto) && this.ventasPorProducto.length > 0 
          ? this.ventasPorProducto.map(item => item.producto || 'Sin nombre')
          : ['Sin datos'],
        noData: {
          text: 'No hay datos disponibles',
          align: 'center',
          verticalAlign: 'middle',
          offsetX: 0,
          offsetY: 0,
          style: {
            color: undefined,
            fontSize: '14px',
            fontFamily: undefined
          }
        },
        colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          itemMargin: { horizontal: 8, vertical: 4 },
          fontSize: '12px',
          markers: {
            width: 10,
            height: 10,
            radius: 5,
            offsetX: -4
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '50%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: isMonto ? 'Total' : 'Total',
                  formatter: () => isMonto 
                    ? `$${this.ventasPorProducto.reduce((sum, item) => sum + parseFloat(item.total_vendido || 0), 0).toLocaleString('es-MX')}`
                    : this.ventasPorProducto.reduce((sum, item) => sum + parseInt(item.unidades_vendidas || 0), 0).toLocaleString('es-MX')
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value) => {
              const total = isMonto 
                ? this.ventasPorProducto.reduce((sum, item) => sum + parseFloat(item.total_vendido || 0), 0)
                : this.ventasPorProducto.reduce((sum, item) => sum + parseInt(item.unidades_vendidas || 0), 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return isMonto 
                ? `$${parseFloat(value).toLocaleString('es-MX')} (${percentage}%)`
                : `${parseInt(value).toLocaleString('es-MX')} (${percentage}%)`;
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val, { seriesIndex }) => {
            const total = isMonto 
              ? this.ventasPorProducto.reduce((sum, item) => sum + parseFloat(item.total_vendido || 0), 0)
              : this.ventasPorProducto.reduce((sum, item) => sum + parseInt(item.unidades_vendidas || 0), 0);
            const value = isMonto 
              ? parseFloat(this.ventasPorProducto[seriesIndex]?.total_vendido || 0)
              : parseInt(this.ventasPorProducto[seriesIndex]?.unidades_vendidas || 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
            return percentage > 5 ? `${percentage}%` : '';
          },
          style: {
            fontSize: '11px',
            fontWeight: 'bold',
            colors: ['#fff']
          }
        },
        responsive: [{
          breakpoint: 768,
          options: {
            legend: {
              position: 'bottom'
            }
          }
        }]
      };
    },
    // Verificar si hay datos válidos para mostrar
   
    // Obtener series para el gráfico
    getChartSeries(type) {
      if (!Array.isArray(this.ventasPorProducto)) return [0];
      
      return this.ventasPorProducto.map(item => {
        const value = type === 'monto' 
          ? parseFloat(item.total_vendido) 
          : parseInt(item.unidades_vendidas, 10);
        return isNaN(value) ? 0 : Math.max(0, value);
      });
    },
    
    // Obtener opciones del gráfico
   
    
    
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      try {
        // Reset chart data to prevent NaN errors during loading
        this.ventasPorProducto = [];
        this.seriesCategorias = [{ data: [] }];
        this.hasData = false;
        
        const response = await reportesNuevos.DASHBOARD_PRODUCTOS({
          fecha_inicio: this.fecha_inicio,
          fecha_fin: this.fecha_fin,
        });

        if (response.data.errors) {
            this.error = Object.values(response.data.errors).join(', ');
            this.ventasPorProducto = [];
            this.productoMasVendido = null;
            this.ventasPorCategoria = [];
            this.seriesCategorias = [{ data: [] }];
            this.chartOptionsCategorias.xaxis.categories = [];
            this.hasData = false;
            return;
        }

        const { ventas_por_producto, producto_mas_vendido, ventas_por_categoria } = response.data;
        
        this.ventasPorProducto = ventas_por_producto;
        this.productoMasVendido = producto_mas_vendido;
        this.ventasPorCategoria = ventas_por_categoria;

        // Update chart data
        this.seriesCategorias = [{ data: this.ventasPorCategoria.map(c => parseFloat(c.total_vendido)) }];
        this.chartOptionsCategorias = { ...this.chartOptionsCategorias, xaxis: { categories: this.ventasPorCategoria.map(c => c.categoria) } };

      } catch (error) {
        console.error('Error al cargar datos:', error);
        this.error = 'No se pudieron cargar los datos. Intente de nuevo más tarde.';
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.cargarDatos();
  },
};
</script>

<style scoped>
/* Estilos específicos para este componente */
</style>
